import { useLayoutEffect, useRef } from 'react';
import { isForeground, subscribeForeground } from './AppStateService';

const MAX_QUEUE = 100;
const MAX_AGE = 7 * 86400000;
const uuid = () => {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// Memory-only bounded observation queue. Delivery on kill/crash is not promised.
// Overflow/expiry explicitly starts a discontinuous capture session; never send
// a gapped suffix that the server could mistake for a complete runtime history.
export function createLifecycleRuntime(owner, backend) {
  let queue = [], sessionId, sequence, active, attachment = null;
  const platform = window.Capacitor?.isNativePlatform?.() ? window.Capacitor.getPlatform?.() : 'web';
  const append = (type, reason) => {
    const position = backend.getEventPosition?.();
    queue.push({ event_id: uuid(), session_id: sessionId, sequence: sequence++, event_type: type, ...(type === 'app.opened' ? { start_reason: reason } : {}),
      platform: ['android', 'ios'].includes(platform) ? platform : 'web', observed_at: new Date().toISOString(),
      ...(position ? { position: { ...position } } : {}) });
  };
  const reset = (reason = 'initial') => {
    queue = []; sessionId = uuid(); sequence = 0; active = isForeground();
    append('app.opened', reason); if (!active) append('app.backgrounded');
  };
  reset();
  const detach = () => {
    const old = attachment; attachment = null;
    if (!old) return;
    old.controller.abort(); clearTimeout(old.timer); old.unsubscribe();
    window.removeEventListener('online', old.online);
  };
  const attach = () => {
    detach();
    const scope = { controller: new AbortController(), busy: false, timer: null, unsubscribe: () => {} };
    attachment = scope;
    const flush = async () => {
      if (attachment !== scope || scope.controller.signal.aborted || scope.busy || !queue.length) return;
      if (Date.now() - Date.parse(queue[0].observed_at) >= MAX_AGE) reset('observation_expired');
      if (navigator.onLine === false) return;
      scope.busy = true;
      const batch = queue.slice(0, 20);
      try {
        await backend.captureAppLifecycle(owner, batch, scope.controller.signal);
        if (attachment === scope && queue[0]?.event_id === batch[0].event_id) queue.splice(0, batch.length);
      } catch (error) {
        if (attachment === scope && !scope.controller.signal.aborted) {
          if (error.code === 'LIFECYCLE_ACCOUNT_CHANGED' || error.status === 401 || error.status === 403) { queue = []; detach(); }
          else if (error.code === 'LIFECYCLE_SEQUENCE' || error.code === 'LIFECYCLE_WINDOW') reset(error.code === 'LIFECYCLE_WINDOW' ? 'observation_expired' : 'sequence_recovery');
        }
      } finally {
        scope.busy = false;
        if (attachment === scope && queue.length) scope.timer = setTimeout(flush, 5000);
      }
    };
    const schedule = () => { clearTimeout(scope.timer); Promise.resolve().then(flush); };
    const change = next => {
      if (next === active) return;
      active = next;
      if (queue.length >= MAX_QUEUE) reset('queue_overflow');
      else append(next ? 'app.foregrounded' : 'app.backgrounded');
      schedule();
    };
    scope.unsubscribe = subscribeForeground(change);
    scope.online = schedule;
    window.addEventListener('online', scope.online);
    change(isForeground());
    schedule();
    return detach;
  };
  return { owner, attach, detach };
}

export function useAppLifecycle(owner, backend) {
  const runtime = useRef(null);
  // Layout cleanup fences async token resolution before new-account effects.
  useLayoutEffect(() => {
    if (!owner) { runtime.current?.detach(); runtime.current = null; return undefined; }
    if (runtime.current?.owner !== owner) {
      runtime.current?.detach();
      runtime.current = createLifecycleRuntime(owner, backend);
    }
    return runtime.current.attach();
  }, [owner, backend]);
}
